'use client';

import { usePaginationLogic } from './usePaginationLogic';

/**
 * Props pour PaginationControls
 */
interface PaginationControlsProps {
  /**
   * Nombre de pages à afficher autour de la page courante
   * @default 2 - Affiche 2 pages avant et 2 pages après la page courante
   */
  padding?: number;
  /**
   * Callback appelé lors du changement de page
   * Utile pour scroller vers le haut des résultats
   */
  onPageChange?: () => void;
  /**
   * Classe CSS additionnelle
   */
  className?: string;
}

/**
 * Composant PaginationControls - Affiche les contrôles de pagination
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'affichage des contrôles de pagination
 * - La logique est déléguée au hook usePaginationLogic
 *
 * Principe SoC:
 * - Séparation complète entre logique (hook) et affichage (composant)
 * - Pas de logique métier dans le composant
 *
 * Principe DRY:
 * - Réutilise le hook pour toute la logique
 * - Pas de duplication avec l'ancienne implémentation
 *
 * Bonnes pratiques React:
 * - Composant fonctionnel avec hooks
 * - Props typées avec TypeScript
 * - Utilise des fragments pour éviter les divs inutiles
 *
 * Fonctionnalités:
 * - Boutons Previous/Next
 * - Toujours afficher la première et dernière page
 * - Pages autour de la page courante (padding)
 * - Dots (...) cliquables pour afficher les multiples de 10
 * - Bloc séparé pour les multiples de 10 (10, 20, 30, etc.)
 * - Liens SEO-friendly avec href
 * - Pas de pagination si une seule page
 *
 * Accessibilité:
 * - aria-label sur la nav
 * - aria-current sur la page active
 * - aria-disabled sur les boutons désactivés
 */
export function PaginationControls({
  padding = 2,
  onPageChange,
  className = '',
}: PaginationControlsProps) {
  const {
    nbPages,
    currentPage,
    isFirstPage,
    isLastPage,
    paginationItems,
    multiplesOfTen,
    showMultiples,
    hasFirstPageInList,
    hasLastPageInList,
    showFirstPageDots,
    showLastPageDots,
    navigateToPage,
    toggleMultiples,
    createURL,
    toRelativeURL,
  } = usePaginationLogic(padding, onPageChange);

  // Ne pas afficher si aucun résultat ou une seule page
  if (nbPages <= 1) {
    return null;
  }

  return (
    <div className={`flex w-full flex-col items-center gap-3 ${className}`}>
      <nav
        className="flex flex-wrap justify-center gap-1"
        aria-label="Pagination"
      >
        {/* Bouton Previous */}
        {!isFirstPage && (
          <a
            href={toRelativeURL(createURL(currentPage - 1))}
            onClick={(e) => {
              e.preventDefault();
              navigateToPage(currentPage - 1);
            }}
            className="btn btn-ghost btn-sm text-gray-500"
            aria-label="Page précédente"
          >
            ‹
          </a>
        )}

        {/* Première page (si pas dans la liste) */}
        {!hasFirstPageInList && (
          <a
            href={toRelativeURL(createURL(0))}
            onClick={(e) => {
              e.preventDefault();
              navigateToPage(0);
            }}
            className="btn btn-ghost btn-sm text-gray-500"
          >
            1
          </a>
        )}

        {/* Dots après première page */}
        {showFirstPageDots && (
          <button
            type="button"
            onClick={toggleMultiples}
            className="btn btn-ghost btn-sm text-gray-500 hover:bg-gray-100"
            aria-label="Afficher les multiples de 10"
          >
            ...
          </button>
        )}

        {/* Pages principales (provenant de usePagination avec padding) */}
        {paginationItems.map((item) => {
          const isActive = currentPage === item.page;

          return (
            <a
              key={item.page}
              href={toRelativeURL(createURL(item.page))}
              onClick={(e) => {
                e.preventDefault();
                navigateToPage(item.page);
              }}
              className={`btn btn-sm ${
                isActive ? 'btn-primary' : 'btn-ghost text-gray-500'
              }`}
              aria-current={isActive ? 'page' : undefined}
            >
              {item.page + 1}
            </a>
          );
        })}

        {/* Dots avant dernière page */}
        {showLastPageDots && (
          <button
            type="button"
            onClick={toggleMultiples}
            className="btn btn-ghost btn-sm text-gray-500 hover:bg-gray-100"
            aria-label="Afficher les multiples de 10"
          >
            ...
          </button>
        )}

        {/* Dernière page (si pas dans la liste) */}
        {!hasLastPageInList && (
          <a
            href={toRelativeURL(createURL(nbPages - 1))}
            onClick={(e) => {
              e.preventDefault();
              navigateToPage(nbPages - 1);
            }}
            className="btn btn-ghost btn-sm text-gray-500"
          >
            {nbPages}
          </a>
        )}

        {/* Bouton Next */}
        {!isLastPage && (
          <a
            href={toRelativeURL(createURL(currentPage + 1))}
            onClick={(e) => {
              e.preventDefault();
              navigateToPage(currentPage + 1);
            }}
            className="btn btn-ghost btn-sm mx-1 text-gray-500"
            aria-label="Page suivante"
          >
            ›
          </a>
        )}
      </nav>

      {/* Bloc des multiples de 10 */}
      {showMultiples && multiplesOfTen.length > 0 && (
        <div className="mt-2 flex flex-wrap justify-center gap-1 border-t border-gray-200 pt-2">
          {multiplesOfTen.map((page) => {
            const isActive = currentPage === page;

            return (
              <a
                key={page}
                href={toRelativeURL(createURL(page))}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToPage(page);
                }}
                className={`btn btn-sm ${
                  isActive ? 'btn-primary' : 'btn-ghost text-gray-500'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                {page + 1}
              </a>
            );
          })}
        </div>
      )}

      {/* Liens cachés pour le SEO */}
      <div className="hidden">
        {multiplesOfTen.map((page) => (
          <a key={`seo-${page}`} href={toRelativeURL(createURL(page))}>
            Page {page + 1}
          </a>
        ))}
      </div>
    </div>
  );
}
